// src/components/ParticipantsAttendanceBar.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../config/supabase';
import LoadingSpinner from './LoadingSpinner';
import AttendanceToggle from './AttendanceToggle';

export default function ParticipantsAttendanceBar({
  lessonId,
  onParticipantClick,
  activeParticipantId,
  disabled = false, // pass disabled={!currentUserId} from App.jsx
}) {
  const [participants, setParticipants] = useState([]); // [{id, student_id, master_student_id, student_master:{...}, attendance_status_id}]
  const [statuses, setStatuses] = useState([]);         // preloaded once for the lesson's school
  const [schoolId, setSchoolId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!lessonId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // 1) Lesson → school_id
      const { data: lesson, error: lessonErr } = await supabase
        .from('lessons')
        .select('id, school_id')
        .eq('id', lessonId)
        .single();
      if (lessonErr) throw lessonErr;
      setSchoolId(lesson.school_id ?? null);

      // 2) Preload statuses once (fast for all tiles)
      const { data: sts, error: stsErr } = await supabase
        .from('attendance_statuses')
        .select('id,label,outcome,allows_makeup,counts_toward_quota_default,billable_default,is_active,sort_order')
        .eq('school_id', lesson.school_id)
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .order('label', { ascending: true });
      if (stsErr) throw stsErr;
      setStatuses(sts || []);

      // 3+4) Fetch participants + attendance in parallel
      const [
        { data: participantsData, error: participantsError },
        { data: attendanceRows, error: attendanceError },
      ] = await Promise.all([
        supabase
          .from('lessons_participants')
          .select(`
            id,
            student_id,
            master_student_id
          `)
          .eq('lesson_id', lessonId)
          .order('joined_at', { ascending: true }),
        supabase
          .from('attendance')
          .select('lessons_participant_id, attendance_status_id')
          .eq('lesson_id', lessonId),
      ]);
      if (participantsError) throw participantsError;
      if (attendanceError) throw attendanceError;

      // Load canonical names from students_master_list (works for solo + group participants)
      const masterIds = Array.from(
        new Set((participantsData || []).map(p => p.master_student_id).filter(Boolean).map(String))
      );
      let masterById = {};
      if (masterIds.length) {
        const { data: masters, error: mastersErr } = await supabase
          .from('students_master_list')
          .select('id, full_name, first_name, last_name')
          .in('id', masterIds);
        if (mastersErr) throw mastersErr;
        masterById = Object.fromEntries((masters || []).map(m => [String(m.id), m]));
      }

      const statusByLpId = {};
      (attendanceRows || []).forEach(r => { statusByLpId[r.lessons_participant_id] = r.attendance_status_id; });

      const merged = (participantsData || []).map(p => ({
        ...p,
        student_master: p.master_student_id ? (masterById[String(p.master_student_id)] ?? null) : null,
        attendance_status_id: statusByLpId[p.id] ?? null,
      }));
      setParticipants(merged);
    } catch (e) {
      console.error('Error fetching participants/statuses:', e);
      setError('Failed to load participants.');
      setParticipants([]);
    } finally {
      setLoading(false);
    }
  }, [lessonId]);

  useEffect(() => {
    if (!lessonId) return;
    fetchAll();

    const lpChannel = supabase
      .channel(`lessons_participants:lesson_id=eq.${lessonId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'lessons_participants', filter: `lesson_id=eq.${lessonId}` },
        () => fetchAll()
      )
      .subscribe();

    const attendanceChannel = supabase
      .channel(`attendance:lesson_id=eq.${lessonId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'attendance', filter: `lesson_id=eq.${lessonId}` },
        (payload) => {
          const row = payload?.new || payload?.old;
          const lpId = row?.lessons_participant_id;
          if (!lpId) return;
          const nextStatusId =
            payload.eventType === 'DELETE'
              ? null
              : (payload?.new?.attendance_status_id ?? null);
          setParticipants(prev =>
            prev.map(p => (p.id === lpId ? { ...p, attendance_status_id: nextStatusId } : p))
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(lpChannel);
      supabase.removeChannel(attendanceChannel);
    };
  }, [lessonId, fetchAll]);

  // Fast local patch on attendance updates – no list refetch
  useEffect(() => {
    const bc = new BroadcastChannel('mms-lessons');
    const onMsg = (e) => {
      const d = e?.data;
      if (d?.type === 'attendance_updated' && d.lessonId === lessonId && d.lessonsParticipantId) {
        setParticipants(prev =>
          prev.map(p =>
            p.id === d.lessonsParticipantId ? { ...p, attendance_status_id: d.payload?.statusId ?? null } : p
          )
        );
      }
    };
    bc.addEventListener('message', onMsg);
    return () => bc.close();
  }, [lessonId]);

  if (loading) return <div className="flex justify-center items-center h-20"><LoadingSpinner /></div>;
  if (error) return <div className="text-red-500 text-center p-4">{error}</div>;
  if (participants.length === 0) {
    return <div className="text-center p-4 text-gray-500 bg-gray-50 rounded-lg">No participants found for this lesson.</div>;
  }

  return (
    <div className="bg-white p-3 rounded-lg">
      <h3 className="text-sm font-semibold text-gray-600 mb-3 px-1">Participants</h3>
      <div className="flex items-center space-x-2 overflow-x-auto pb-2">
        {participants.map((p) => {
          const sm = p.student_master;
          if (!sm) return null;
          const masterId = String(sm.id);
          const isActive = masterId === String(activeParticipantId ?? '');
          const first = (sm.first_name || '').trim();
          const last = (sm.last_name || '').trim();
          const full =
            (sm.full_name && String(sm.full_name).trim()) ||
            `${first} ${last}`.trim() ||
            'Student';
          const firstForTile = first || full.split(' ')[0] || 'Student';
          return (
            <button
              key={p.id}
              onClick={() => onParticipantClick?.(masterId, p.id)}
              className={`flex-shrink-0 flex flex-col items-center justify-center p-2 rounded-lg transition-colors duration-200
                w-20 h-20 text-center relative group
                ${isActive ? 'bg-blue-100 ring-2 ring-blue-500' : 'bg-gray-50 hover:bg-gray-100'}`}
              title={full}
            >
              {/* Fast, compact, live control; prehydrated and optimistic */}
              <div className="absolute top-1 right-1" onClick={(e) => e.stopPropagation()}>
                <AttendanceToggle
                  lessonId={lessonId}
                  lessonsParticipantId={p.id}
                  variant="compact"
                  disabled={disabled}
                  prefetch={{
                    // IMPORTANT: include the key so AttendanceToggle can skip self-load per tile
                    schoolId: schoolId ?? null,
                    statuses,
                    initialStatusId: p.attendance_status_id ?? null,
                  }}
                />
              </div>

              <img
                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(first || '')}+${encodeURIComponent(last || '')}&background=random`}
                alt={full}
                className="w-10 h-10 rounded-full object-cover mb-1 border-2 border-white"
              />
              <span className="text-xs font-medium text-gray-700 truncate w-full">
                {firstForTile}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}